import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Flex, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { FeatureFlags } from '../../../../../../../../../components/Authorizer';
import useCompanyFeatures from '../../../../../../../../../components/Authorizer/useCompanyFeatures';
import { buildQuickFilterOptions, setAdvancedFilterValues } from '../../../../../../../../../components/search/util';
import LoadingSpinner from '../../../../../../../../../components/Spinner';
import { CypressIds } from '../../../../../../../../../constants/cypressIds';
import {
  activitySearch,
  getActivitySubCommentsBatch,
  getCommentSubCommentsBatch,
  selectIsFetchingActivitySubComments,
  selectIsFetchingCommentSubComments
} from '../../../../../../../../../redux/modules/activityLog';
import { itemSearch } from '../../../../../../../../../redux/modules/items';
import { useCompanyCodes, useCompanyUsers } from '../../../../../../../../../services/company-service';
import { useAllInsuredRecords } from '../../../../../../../../../services/insured-service';
import { sortAlphaNumeric } from '../../../../../../../../../utils/arrayUtil';
import ActivityLogRow from '../ActivityLogRow';

import advancedFilters from './advancedFilters';
import quickFilters from './quickFilters';

const ActivityLogBody = ({ companyId, insuredId, recordId, jobId }) => {
  const dispatch = useDispatch();
  const { data: companyCodes, isLoading: companyCodesLoading } = useCompanyCodes(companyId);
  const { data: companyUsers, isLoading: companyUsersLoading } = useCompanyUsers(companyId);
  const { data: allRecords, isLoading: allRecordsLoading } = useAllInsuredRecords({ insuredId });

  const allRecordsData = useMemo(() => {
    if (allRecordsLoading) {
      return [];
    }

    return allRecords;
  }, [allRecords, allRecordsLoading]);

  const activities = useSelector(activitySearch.selectData);
  const itemsData = useSelector(itemSearch.selectData);
  const activitiesPageInfo = useSelector(activitySearch.selectPageInfo);
  const isLoading = useSelector(activitySearch.selectIsLoading);
  const isFetchingActivitySubComments = useSelector(selectIsFetchingActivitySubComments);
  const isFetchingCommentSubComments = useSelector(selectIsFetchingCommentSubComments);
  const { companyHasFeature: hasItemsEnabled } = useCompanyFeatures(FeatureFlags.Items);

  useEffect(() => {
    if (companyCodesLoading || companyUsersLoading || allRecordsLoading) return;

    dispatch(activitySearch.setQuickFilterOptions(buildQuickFilterOptions(quickFilters, {
      codeApplied: companyCodes,
      user: companyUsers.sort(sortAlphaNumeric('userFirstName')),
      record: allRecordsData
    })));

    dispatch(activitySearch.setAdvancedFilterOptions(setAdvancedFilterValues(advancedFilters, {
      code: { subProperty: 'letter', values: companyCodes },
      user: { subProperty: 'email', values: companyUsers }
    })));

    // set the activity log quick filter to the selected record anytime the record id changes
    if (recordId && !hasItemsEnabled) {
      dispatch(activitySearch.updateQuickFilters(
        'record',
        allRecordsData.filter(r => r.id === recordId)
      ));
    } else {
      dispatch(activitySearch.getPage());
    }
  }, [companyCodes, companyUsers, allRecordsData, dispatch, recordId]);

  useEffect(() => {
    if (isFetchingActivitySubComments) return;

    const activityIds = activities
      .filter(a => !a.comments && a.activityType === 'activity')
      .map(a => a.id);

    dispatch(getActivitySubCommentsBatch(activityIds));
  }, [activities, dispatch, isFetchingActivitySubComments]);

  useEffect(() => {
    if (isFetchingCommentSubComments) return;

    const commentIds = activities
      .filter(a => !a.comments && a.activityType === 'comment')
      .map(a => a.id);

    dispatch(getCommentSubCommentsBatch(commentIds));
  }, [activities, dispatch, isFetchingCommentSubComments]);

  if ((!activities?.length && isLoading) || !itemsData) {
    return (
      <Flex
        justifyContent="center"
        minHeight="400px"
        position="relative"
      >
        <LoadingSpinner isInline />
      </Flex>
    );
  }

  return (
    <Box
      data-cy={CypressIds.activityLogBody}
      h="400px"
      id="activity-log-body-box"
      overflowX="auto"
    >
      { activities?.length
        ? (
          <>
            { (activities || []).map(activity => (
              <ActivityLogRow
                activity={activity}
                companyId={companyId}
                insuredId={insuredId}
                jobId={jobId}
                key={activity.id}
                recordId={recordId}
              />
            ))}
            {activitiesPageInfo.currentPage < activitiesPageInfo.lastPage
              ? (
                <Flex
                  justifyContent="center"
                  maxW="200px"
                  mx="auto"
                  my={2}
                  onClick={() => dispatch(activitySearch.getPage({ page: activitiesPageInfo.currentPage + 1 }))}
                >
                  <Text
                    _hover={{ borderBottom: '1px', borderBottomColor: 'primaryScale.600' }}
                    color="greyScale.800"
                    cursor="pointer"
                  >
                    { isLoading ? 'Loading...' : 'Show more activities'}
                  </Text>
                </Flex>
              )
              : (
                <Box my={2}>
                  <Text color="greyScale.800" textStyle="bodyMd">
                    {`Showing All (${activities.length})`}
                  </Text>
                </Box>
              )}
          </>
        )
        : (
          <Box
            color="greyScale.800"
            position="relative"
            top="33%"
          >
            No Actions or Comments
          </Box>
        )}

    </Box>
  );
};

ActivityLogBody.propTypes = {
  companyId: PropTypes.number.isRequired,
  insuredId: PropTypes.number.isRequired,
  recordId: PropTypes.number.isRequired,
  jobId: PropTypes.number.isRequired
};

export default ActivityLogBody;
